import createClient, { type Middleware } from 'openapi-fetch'

import { reportReachable } from '@/lib/net/connectivity'

import { apiErrorFromResponse, isAbortError, networkError } from './errors'
import { CLIENT_ID, getCsrfToken } from './session'
import type { paths } from './types'

export const API_BASE = '/api/v1'

/** Absolute so Request objects can be built outside a browser page too (tests, workers). */
export const API_ORIGIN_BASE = `${globalThis.location?.origin ?? ''}${API_BASE}`

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])
const GATEWAY_ERRORS = new Set([502, 503, 504])

/** Header that marks background refetches so they never extend the idle session (AUTH-3). */
export const PASSIVE = { 'Tr-Activity': 'passive' } as const

const middleware: Middleware = {
  onRequest({ request }) {
    if (!SAFE_METHODS.has(request.method)) {
      const token = getCsrfToken()
      if (token) request.headers.set('X-CSRF-Token', token)
    }
    request.headers.set('Tr-Client-Id', CLIENT_ID)
    return request
  },
  async onResponse({ response }) {
    // A gateway error means the proxy answered but the API did not.
    reportReachable(!GATEWAY_ERRORS.has(response.status))
    if (!response.ok) throw await apiErrorFromResponse(response)
    return undefined
  },
  onError({ error }) {
    // Returning nothing rethrows the original error, which keeps Query's cancellation intact.
    if (isAbortError(error)) return undefined
    reportReachable(false)
    return networkError(error)
  },
}

/**
 * The typed client. Every call either resolves with data or rejects with ApiError
 * (lib/api/errors.ts), so callers never branch on `{ error }`.
 */
export const api = createClient<paths>({
  baseUrl: API_ORIGIN_BASE,
  credentials: 'include',
  // Resolved at call time so tests and MSW can swap the global fetch.
  fetch: (input: Request, init?: RequestInit) => globalThis.fetch(input, init),
})
api.use(middleware)

/** Unwraps an openapi-fetch result whose errors were already thrown by the middleware. */
export async function data<T>(promise: Promise<{ data?: T; response: Response }>): Promise<T> {
  const { data: body } = await promise
  return body as T
}
